import React, { useCallback, useEffect } from "react";
import { ReleasePageWrapper } from "../ReleasePageWrapper";
import { useNavigate, useSearchParams } from "react-router-dom";
import { WizardService } from "../../../services/WizardService";
import {
  Button,
  Col,
  Row,
  Space,
  Table,
  TablePaginationConfig,
  message,
  Drawer,
  Popconfirm,
} from "antd";
import {
  DropdownAction,
  DefaultColumnBuilder as builder,
} from "../../components/ColumnBuilder";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Plugin } from "../../../types/plugin";
import { TableQueryFilter } from "../../components/TableQueryFilter";
import { PluginForm } from "./PluginForm";
import { TableColumnSelector } from "../../components/TableColumnSelector";

export const PluginListPage = () => {
  const api = WizardService.Plugin;
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [records, setRecords] = React.useState<Plugin[]>([]);
  const [selectedRecords, setSelectedRecords] = React.useState<number[]>([]);
  const [editing, setEditing] = React.useState<number | undefined>(undefined);
  const [visibleColumns, setVisibleColumns] = React.useState<string[]>([]);

const [pagination, setPagination] = React.useState<TablePaginationConfig>({
    current: searchParams.get("page") !== null ? +searchParams.get("page")! : 1,
    pageSize: searchParams.get("per") !== null ? +searchParams.get("per")! : 10,
    showSizeChanger: true,
  });

  const viewRecord = (id: number) => {
    navigate(id.toString());
  };

  const editRecord = (id: number) => {
    setEditing(id);
  };

  const copyRecord = (id: number) => {
    setIsLoading(true);
    api
      .copy(id)
      .then((copiedPlugin) => {
        return api.all(searchParams).then((response) => {
          setRecords(response.items || []);
          setPagination((state) => ({
            ...state,
            current: response.metadata.page,
            pageSize: response.metadata.per,
            total: response.metadata.total,
          }));
          setEditing(copiedPlugin.id);
        });
      })
      .catch((error) => {
        message.error(error.message);
        const originalRecord = records.find((r) => r.id === id);
        if (originalRecord) {
          const copiedRecord: Plugin = {
            ...originalRecord,
            id: undefined,
            // Modify the name to indicate it's a copy
            name: `${originalRecord.name} (Copy)`,
          };
          setRecords([...records, copiedRecord]);
          setEditing(undefined);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const allColumns = [
    builder.id,
    builder.text("Status", "status", true),
    builder.text("Display Name", "name"),
    builder.version,
    builder.date("Created At", "createdAt"),
    builder.dropdown(
      [DropdownAction.VIEW, DropdownAction.EDIT, DropdownAction.COPY, DropdownAction.DELETE],
      (id, action) => {
        console.log(id, action);
        switch (action) {
          case DropdownAction.VIEW:
            return viewRecord(id);
          case DropdownAction.EDIT:
            return editRecord(id);
          case DropdownAction.COPY:
            return copyRecord(id);
          case DropdownAction.DELETE:
            return removeRecord(id);
        }
      }
    ),
  ];

  const columns = () => {
    if (visibleColumns.length > 0) {
      return allColumns.filter(
        (c) => c.fixed === "right" || visibleColumns.includes(c.dataIndex?.toString() || "")
      );
    }
    return allColumns;
  };

  const rowSelection = {
    onChange: (_: React.Key[], selectedRows: any[]) => {
      setSelectedRecords(selectedRows.map((row) => row.key));
    },
  };

  const saveRecord = (record: Plugin) => {
    setIsLoading(true);
    let promise: Promise<any>;
    if (record.id !== undefined && record.id > 0) {
      promise = api.update(record.id, record);
    } else {
      promise = api.create(record);
    }

    promise
      .then((_) => fetch())
      .then(() => {
        setEditing(undefined);
      })
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  };

  const removeRecord = (id: number) => {
    setIsLoading(true);
    api
      .delete(id)
      .then((_) => fetch())
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  };

  const removeSelectedRecords = () => {
    setIsLoading(true);
    Promise.all(selectedRecords.map((id) => api.delete(id)))
      .then((_) => fetch())
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  };

  // Callbacks
  const fetch = useCallback(() => {
    setSelectedRecords([]);
    setIsLoading(true);
    api
      .all(searchParams)
      .then((response) => {
        setRecords(response.items || []);
        setPagination((state) => ({
          ...state,
          current: response.metadata.page,
          pageSize: response.metadata.per,
          total: response.metadata.total,
        }));
      })
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  }, [searchParams, api]);

  // Hooks
  useEffect(() => {
    fetch();
  }, [fetch]);

  return (
    <ReleasePageWrapper>
      <Row
        style={{
          marginTop: "10px",
          marginBottom: "10px",
          paddingBottom: "8px",
        }}
      >
        <Col flex="auto">
          <TableQueryFilter filter={["framework", "pattern", "version"]} />
        </Col>
        <Col flex="right">
          <Space>
            <TableColumnSelector
              columns={allColumns}
              onChange={setVisibleColumns}
              storageKey="plugin-table-columns"
            />
            <Button
              type="primary"
              icon={<PlusOutlined  />}
              onClick={(_) => setEditing(0)}
            >
              Create
            </Button>
            <Popconfirm
              title="Delete plugins"
              description="Are you sure to delete selected plugin?"
              onConfirm={removeSelectedRecords}
              okText="Yes"
              cancelText="No"
            >
              <Button
                danger
                icon={<DeleteOutlined  />}
                disabled={selectedRecords.length === 0}
              >
                Delete
              </Button>
            </Popconfirm>
          </Space>
        </Col>
      </Row>
      <Row>
        <Col flex="100%">
          <Table
            loading={isLoading}
            columns={columns()}
            pagination={pagination}
            onRow={(record) => ({
              onClick: () => {
                if (record.id !== undefined) viewRecord(record.id);
              },
            })}
            onChange={(pagination) => {
              setPagination(pagination);
              const params = new URLSearchParams(searchParams);

              pagination.current
                ? params.set("page", pagination.current.toString())
                : params.delete("page");
              pagination.pageSize
                ? params.set("per", pagination.pageSize.toString())
                : params.delete("per");

              setSearchParams(params);
            }}
            dataSource={records.map((record) => ({
              ...record,
              key: record.id,
            }))}
            rowSelection={rowSelection}
          />
        </Col>
      </Row>
      <Drawer
        title={
          editing !== undefined && editing > 0
            ? "Edit Plugin #" + editing
            : "Create a new Plugin"
        }
        width={600}
        onClose={(_) => setEditing(undefined)}
        open={editing !== undefined}
        styles={{
          body: {
            paddingBottom: 80,
          },
        }}
      >
        <PluginForm
          plugin={records.filter((r) => r.id === editing)[0]}
          isLoading={isLoading}
          onFinish={saveRecord}
        />
      </Drawer>
    </ReleasePageWrapper>
  );
};
